import React, {CSSProperties, useCallback, useEffect, useMemo, useState} from 'react';
import {Grid} from '@mui/material';
import {useTranslation} from "react-i18next";
import {useTemplate} from "../../../../context/TemplateContext";
import {useMobile} from "../../../../context/MobileContext";
import {ImageInput, ImageValue} from "../../../../models/Template";
import {useSelector} from "react-redux";
import {RootState} from "../../../../utils/store";
import ImagePreUploadDialog from "./../helpers/ImagePreUploadDialog";
import {toast} from "react-toastify";
import Asset from "../../../../models/Asset";
import Folder from "../../../../models/Folder";
import {useApiService} from "../../../../context/ApiServiceContext";
import AssetBoxImage from "./AssetBox/AssetBoxImage";
import AssetBoxVideo from "./AssetBox/AssetBoxVideo";
import AssetBoxFolder from "./AssetBox/AssetBoxFolder";
import AssetBoxUpload from "./AssetBox/AssetBoxUpload";
import AssetBoxBack from "./AssetBox/AssetBoxBack";
import AssetBoxSkeleton from "./AssetBox/AssetBoxSkeleton";
import AssetBoxNewFolder from "./AssetBox/AssetBoxNewFolder";
import AiMagicDialog from "../../AiMagic/AiMagicDialog";
import {AxiosError} from "axios";
import {isVideoAsset, isImageAsset} from "../../../../models/Asset";
import {isUserLayerId} from "../../../../scene/sceneToKonva";
import {UsedAssetMap} from "./hooks/useUsedAssets";
import logger from "../../../../utils/logger";

type FolderItem = Folder & { itemType: string } | Asset & { itemType: string };

type FolderApiResult = FolderItem[];

interface ImageSelectorProps {
    elementKey: string;
    value: ImageValue;
    onChange: (asset: Asset, key: string) => void;
    folderId: string;
    onFolderClick: (folder: Folder) => void;
    style?: CSSProperties;
    showBackButton?: boolean;
    handleBackClick?: () => void;
    searchResults?: Asset[] | null;
    isSearchActive?: boolean;
    handleClose: () => void;
    usedAssetMap?: UsedAssetMap;
    showUnusedOnly?: boolean;
}

const ImageSelectionGrid: React.FC<ImageSelectorProps> = ({
                                                              elementKey,
                                                              value,
                                                              onChange,
                                                              folderId,
                                                              onFolderClick,
                                                              style,
                                                              showBackButton = false,
                                                              handleBackClick,
                                                              searchResults,
                                                              isSearchActive = false,
                                                              handleClose,
                                                              usedAssetMap,
                                                              showUnusedOnly = false,
                                                          }) => {
    const apiService = useApiService();
    const {t} = useTranslation();
    const {template} = useTemplate();
    const {isMobile} = useMobile();
    const user = useSelector((state: RootState) => state.user.user);

    // Calculate video permission
    const allowVideo = useMemo(() => {
        // Check team permission
        const teamSupportsVideo = user?.team?.supportsVideo || false;
        logger.debug('[VIDEO PERMISSION] Team supports video:', teamSupportsVideo, 'Team:', user?.team);

        if (!teamSupportsVideo) {
            logger.debug('[VIDEO PERMISSION] ❌ Team does not support video');
            return false;
        }

        // Check input permission
        const currentInput = template?.inputs?.[elementKey] as ImageInput;
        logger.debug('[VIDEO PERMISSION] Current input:', elementKey, currentInput);

        if (!currentInput || currentInput.type !== 'image') {
            logger.debug('[VIDEO PERMISSION] ❌ Input not found or not an image');
            return false;
        }

        const inputVideoEnabled = currentInput.videoEnabled || false;
        logger.debug('[VIDEO PERMISSION] Input videoEnabled:', inputVideoEnabled);
        logger.debug('[VIDEO PERMISSION] ✅ Final allowVideo:', inputVideoEnabled);

        return inputVideoEnabled;
    }, [user, template, elementKey]);

    // Same two-part gate as video: a team entitlement (a positive daily limit)
    // AND the admin opting this specific input in.
    const allowAiEdit = useMemo(() => {
        if ((user?.team?.aiEditDailyLimit ?? 0) <= 0) return false;
        const currentInput = template?.inputs?.[elementKey] as ImageInput;
        return currentInput?.type === 'image' && !!currentInput.aiEditable;
    }, [user, template, elementKey]);

    const aiPromptVariants = useMemo(() => {
        const currentInput = template?.inputs?.[elementKey] as ImageInput;
        return currentInput?.type === 'image' ? currentInput.aiPromptVariants : undefined;
    }, [template, elementKey]);

    const aiRemoveBackground = useMemo(() => {
        const currentInput = template?.inputs?.[elementKey] as ImageInput;
        return currentInput?.type === 'image' ? currentInput.aiRemoveBackground : undefined;
    }, [template, elementKey]);

    // The "infinite scroll" states
    const [loading, setLoading] = useState<boolean>(false);
    const [data, setData] = useState<{
        [folderId: string]: {
            hasMore: boolean;
            items: FolderItem[] | null;
            page: number;
            isActive: boolean;
        }
    }>({[folderId]: {hasMore: true, items: null, page: 1, isActive: true}}); // Initialize with `initialData`

    // For file upload logic
    const [dimensions, setDimensions] = useState({width: 0, height: 0});
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    /** The library photo the user asked to run the AI edit on. */
    const [aiAsset, setAiAsset] = useState<Asset | null>(null);
    const [onChangeEvent, setOnChangeEvent] = useState<[Asset, string] | null>(null);

    // This is the selected "asset" from the grid, if any
    const [selectedImage, setSelectedImage] = useState<ImageValue>(value);

    // In your original code, you had an array of "options" typed as ImageValue[];
    // We now derive "folder vs asset" from `items`.
    // If it's a folder, you might show a folder tile.
    // If it's an asset (type="image"), treat it like an ImageValue option.

    // ---------------------------------------------
    // 1) Fetch logic with infinite scroll
    // ---------------------------------------------
    const maxPerPage = 14;

    useEffect(() => {
        if (!data[folderId]) {
            setData(prev => ({
                ...prev,
                [folderId]: {hasMore: false, items: null, page: 1, isActive: true}
            }))
        } else {
            setData(prev => ({
                ...prev,
                [folderId]: {
                    ...prev[folderId],
                    isActive: true
                }
            }))
        }
    }, [folderId])

    const fetchFolderData = useCallback(async (pageToFetch: number) => {
        if (!folderId) return;
        try {
            setLoading(true);
            const response = await apiService.api.get<FolderApiResult>(
                `/folder/${folderId}`,
                {
                    params: {
                        page: pageToFetch,
                        perPage: maxPerPage,
                    },
                }
            );
            const data = response.data;

            setData(prev => ({
                ...prev,
                [folderId]: {
                    ...prev[folderId],
                    items: [
                        ...prev[folderId].items ?? [],
                        ...data
                    ],
                    hasMore: data.length >= maxPerPage
                }
            }))
        } catch (err) {
            toast.error(t("error.folderLoad") || "Could not load folder items");
        } finally {
            setLoading(false);
        }
    }, [folderId, t, apiService]);

    // On mount or whenever page changes, fetch data
    useEffect(() => {
        // Skip fetching if we're using search results
        if (isSearchActive) return;

        const currentData = data[folderId]
        if ((currentData?.page > 1 && currentData.isActive && currentData.hasMore) ||
            (currentData?.page == 1 && !currentData?.items)) {
            fetchFolderData(data[folderId].page);
        }
    }, [folderId, data[folderId]?.page, fetchFolderData, isSearchActive]);


    // In a typical infinite scroll scenario, you'd watch for
    // the "scroll to bottom" event in a scrollable container
    // and then call setPage(prev => prev+1). For now, we do a simple
    // check in an onScroll or a 'Load more' button approach.
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        // Skip infinite scroll if we're using search results
        if (isSearchActive) return;

        const target = e.currentTarget;
        const nearBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
        if (nearBottom && !loading && data[folderId].hasMore) {
            setData(prev => ({
                ...prev,
                [folderId]: {
                    ...prev[folderId],
                    page: prev[folderId].page + 1
                }
            }))
        }
    };

    // ---------------------------------------------
    // 2) Handle file uploading
    // ---------------------------------------------
    const handleFileSelect = (file: File | null) => {
        setSelectedFile(file);
        setOpenDialog(true);
    };

    const handleNewFolderCreated = (folder: Folder) => {
        handleAddItems(folder, 'folder')
    };

    const onUploadImageSuccess = (asset: Asset) => {
        handleAddItems(asset, 'asset')
    };

    const handleAddItems = (item: Asset | Folder, itemType: 'asset' | 'folder') => {
        setData(prev => ({
            ...prev,
            [folderId]: {
                ...prev[folderId],
                items: [
                    {...item, itemType: itemType},
                    ...prev[folderId].items ?? []
                ]
            }
        }))
    }

    // Stabilized (useCallback) so the memoized AssetBox tiles don't re-render on
    // every grid render just because this handler was a fresh function.
    const handleEntityEdit = useCallback((entity: Folder | Asset) => {
        setData(prev => ({
            ...prev,
            [folderId]: {
                ...prev[folderId],
                items: prev[folderId].items?.map(f => {
                    if (f.id == entity.id) {
                        f.name = entity.name
                    }
                    return f;
                }) ?? []
            }
        }))
    }, [folderId])

    const handleFolderDelete = (folder: Folder) => {
        apiService.api
            .delete(`/folder/${folder.id}`)
            .then((response) => {
                setData(prev => ({
                    ...prev,
                    [folderId]: {
                        ...prev[folderId],
                        items: prev[folderId].items?.filter(f => {
                            return f.id != folder.id;
                        }) ?? []
                    }
                }))
                toast.success('התיקיה נמחקה בהצלחה');
            })
            .catch((e: AxiosError<{ code: string }>) => {
                logger.debug(e)
                toast.error('תקלה בעת מחיקת התיקיה');
            });
    }

    const handleImageDelete = useCallback((asset: Asset) => {
        apiService.api
            .delete(`/asset/${asset.id}`)
            .then((response) => {
                setData(prev => ({
                    ...prev,
                    [folderId]: {
                        ...prev[folderId],
                        items: prev[folderId].items?.filter(f => {
                            return f.id != asset.id;
                        }) ?? []
                    }
                }))
                toast.success('התמונה נמחקה בהצלחה');
            })
            .catch((e: AxiosError<{ code: string }>) => {
                logger.debug(e)
                toast.error('תקלה בעת מחיקת התמונה');
            });
    }, [apiService, folderId])

    // ---------------------------------------------
    // 3) Effects for measuring the default image
    // ---------------------------------------------
    useEffect(() => {
        const imageInput = template?.inputs?.[elementKey] as ImageInput;
        if (imageInput?.default?.thumbnail) {
            const img = new Image();
            img.onload = () => {
                setDimensions({width: img.width, height: img.height});
            };
            img.src = imageInput.default.thumbnail;
        }
    }, [elementKey, template]);

    useEffect(() => {
        if (onChangeEvent) {
            onChange(...onChangeEvent);
            setOnChangeEvent(null);
            setSelectedImage(onChangeEvent[0]);
        }
    }, [onChangeEvent, onChange]);

    const handleImageClick = useCallback((asset: Asset) => {
        // Convert asset to your ImageValue shape
        const val: ImageValue = {
            assetId: asset.id,
            name: asset.name,
            highRes: asset.highRes,
            thumbnail: asset.thumbnail
        };
        setSelectedImage(val);
        onChange(asset, elementKey);
    }, [onChange, elementKey]);

    const handleImageDoubleClick = useCallback((asset: Asset) => {
        handleImageClick(asset);
        handleClose();
    }, [handleImageClick, handleClose]);

    const handleFolderClick = (folder: Folder) => {
        setData(prev => ({
            ...prev,
            [folderId]: {
                ...prev[folderId],
                isActive: false
            }
        }))
        onFolderClick(folder)
    }

    // Decide which items to render based on whether we're in search mode
    const itemsToRender = useMemo(() => {
        const raw = isSearchActive && searchResults
            ? searchResults.map(asset => ({...asset, itemType: 'asset'}))
            : data[folderId]?.items || [];

        if (!showUnusedOnly || !usedAssetMap) return raw;

        return raw.filter(item => {
            if (item.itemType === 'folder') return true;
            const asset = item as Asset;
            return !usedAssetMap[asset.thumbnail];
        });
    }, [isSearchActive, searchResults, data[folderId]?.items, showUnusedOnly, usedAssetMap]);

    // ---------------------------------------------
    // 4) Rendering
    // ---------------------------------------------
    return (
        <>
            <Grid
                container
                alignContent={"flex-start"}
                sx={{
                    overflowY: loading ? "hidden" : "auto",
                    ...style,
                    height: loading ? style?.maxHeight ?? "800px" : style?.height,
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.7)',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: 'rgba(255, 255, 255, 0.7)',
                    },
                    // For Firefox
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgba(255, 255, 255, 0.7) transparent',
                }}
                spacing={1}
                onScroll={handleScroll}
            >
                {!loading && showBackButton && handleBackClick && <AssetBoxBack
                    handleBackClick={handleBackClick}
                />}

                {!loading && !isSearchActive && <AssetBoxUpload
                    handleFileSelect={handleFileSelect}
                    allowVideo={allowVideo}
                />}

                {!loading && !isSearchActive && <AssetBoxNewFolder
                    parentFolderId={folderId}
                    onNewFolderCreated={handleNewFolderCreated}
                />}

                {itemsToRender.map((item, index) => {
                    if (item.itemType === "folder" && !isSearchActive) {
                        const folder = item as Folder;
                        return (
                            <AssetBoxFolder
                                key={`folder-${folder.id}-${index}`}
                                folder={folder}
                                handleFolderClick={handleFolderClick}
                                handleFolderDelete={handleFolderDelete}
                                handleFolderEdit={handleEntityEdit}/>
                        );
                    } else {
                        const asset = item as Asset;
                        const isSelected = selectedImage?.thumbnail === asset.thumbnail;

                        // Render video asset. A USER LAYER is image-only, so its
                        // gallery hides them outright rather than offering a tile
                        // that would be rejected on tap.
                        if (isVideoAsset(asset)) {
                            if (isUserLayerId(elementKey)) return null;
                            return (
                                <AssetBoxVideo
                                    key={`asset-${asset.id}-${index}`}
                                    isSelected={isSelected}
                                    asset={asset}
                                    handleImageClick={handleImageClick}
                                    handleImageDoubleClick={handleImageDoubleClick}
                                    handleImageDelete={handleImageDelete}
                                    handleImageEdit={handleEntityEdit}
                                    usedByInputs={usedAssetMap?.[asset.thumbnail]}/>
                            );
                        }

                        // Render image asset (default)
                        return (
                            <AssetBoxImage
                                key={`asset-${asset.id}-${index}`}
                                isSelected={isSelected}
                                asset={asset}
                                handleImageClick={handleImageClick}
                                handleImageDoubleClick={handleImageDoubleClick}
                                handleImageDelete={handleImageDelete}
                                handleImageEdit={handleEntityEdit}
                                usedByInputs={usedAssetMap?.[asset.thumbnail]}
                                onAiEdit={allowAiEdit ? setAiAsset : undefined}/>
                        );
                    }
                })}

                {loading && (
                    Array.from({length: 5 + Math.random() * (maxPerPage - 5)}).map((_, index) => (
                        <AssetBoxSkeleton key={index}/>
                    ))
                )}
            </Grid>

            {openDialog && (
                <ImagePreUploadDialog
                    open={openDialog}
                    selectedFile={selectedFile}
                    folderId={folderId}
                    dimensions={dimensions}
                    onClose={() => {
                        setOpenDialog(false);
                        setSelectedFile(null); // don't keep a stale file across opens
                    }}
                    onUploadSuccess={onUploadImageSuccess}
                    inputKey={elementKey}
                    aiEditable={allowAiEdit}
                    aiPromptVariants={aiPromptVariants}
                    aiRemoveBackground={aiRemoveBackground}
                    onAiAccept={(asset) => {
                        onUploadImageSuccess(asset);
                        handleImageClick(asset);
                    }}
                    onSelectAsset={handleImageClick}
                />
            )}

            {/* AI on a photo ALREADY in the library. The backend accepts any
                asset in the field's folder as a source — only the UI used to
                require that the photo had just been uploaded, which made the
                feature unreachable for a library's worth of existing photos. */}
            {aiAsset && (
                <AiMagicDialog
                    open
                    api={apiService.api}
                    templateId={template?.id ?? ''}
                    inputKey={elementKey}
                    sourceAsset={aiAsset}
                    aiPromptVariants={aiPromptVariants}
                    frameSize={dimensions}
                    folderId={folderId}
                    removeBackground={aiRemoveBackground}
                    onAccept={(asset) => {
                        onUploadImageSuccess(asset);
                        handleImageClick(asset);
                        setAiAsset(null);
                    }}
                    onClose={() => setAiAsset(null)}
                />
            )}
        </>
    );
};

export default ImageSelectionGrid;